import styled, { useTheme } from "styled-components";
import { Link } from "react-router-dom";

type Dir = "rtl" | "ltr";

export default function HomeHero() {
  const { dir } = useTheme() as { dir: Dir };

  return (
    <Hero $dir={dir}>
      <Copy>
        <Badge>✨ {dir === "rtl" ? "نورٌ على نور" : "Noor Ala Noor"}</Badge>
        <Title>{dir === "rtl" ? "استمع للقرآن الكريم" : "Listen to the Holy Quran"}</Title>
        <Subtitle>
          {dir === "rtl"
            ? "وجهتك الأولى لقراءة القرآن والاستماع للتلاوات والأدعية"
            : "Your go-to platform for Quran reading, recitation, and dua listening."}
        </Subtitle>
        <Cta to="/listen">{dir === "rtl" ? "ابدأ الاستماع" : "Start Listening"}</Cta>
      </Copy>

      <Art>
        <Glow aria-hidden />
        <Img src="/images/quran-illustration.png" alt="Quran illustration" />
      </Art>
    </Hero>
  );
}

const Hero = styled.section<{ $dir: Dir }>`
  position: relative; overflow: hidden; display: flex; align-items: center; justify-content: space-between;
  gap: 32px; padding: clamp(24px, 4vw, 48px) clamp(24px, 4vw, 56px); border-radius: 28px;
  background: var(--surface); border: 1px solid var(--border); 
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04); 
  margin-bottom: clamp(32px, 5vw, 56px);
  flex-direction: row;
  backdrop-filter: blur(12px);

  &::after {
    content: ''; position: absolute; inset-inline: -20%; inset-block-start: -40%; height: 140%;
    border-radius: 999px; filter: blur(60px); opacity: .18;
    background:
      radial-gradient(50% 50% at 30% 30%, color-mix(in oklab, var(--primary) 35%, transparent) 0%, transparent 65%),
      radial-gradient(45% 45% at 70% 50%, color-mix(in oklab, var(--gradient-end) 40%, transparent) 0%, transparent 75%);
    pointer-events: none;
    animation: float 8s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(2deg); }
  }

  @media (max-width: 768px) {
    display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 16px;
    padding: 20px 20px; border-radius: 20px; margin-bottom: 24px;
    &::after { opacity: .12; }
  }
`;

const Copy = styled.div` flex: 1 1 520px; min-width: 0; `;
const Badge = styled.span`
  display:inline-block; padding:8px 14px; border-radius:999px; font-size:13px; font-weight:500; letter-spacing:.3px;
  color:var(--primary); border:1px solid color-mix(in oklab, var(--primary) 20%, transparent);
  background: linear-gradient(135deg, color-mix(in oklab, var(--primary) 12%, var(--surface)), color-mix(in oklab, var(--primary) 8%, var(--surface))); 
  box-shadow: 0 2px 8px rgba(0,0,0,.04), 0 1px 2px rgba(0,0,0,.02) inset;
  transition: all 0.2s ease;
  &:hover { transform: scale(1.02); }
`;
const Title = styled.h1` 
  margin:12px 0 8px; 
  font-size:clamp(1.6rem, 3vw, 2.4rem); 
  line-height:1.15; 
  color:var(--text); 
  font-weight:600; 
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--text), color-mix(in oklab, var(--text) 70%, var(--primary)));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;
const Subtitle = styled.p`
  margin:0 0 20px; color:var(--muted); font-size:clamp(1rem, 1.5vw, 1.1rem); line-height: 1.6;
  @media (max-width: 768px) {
    display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; margin-bottom: 16px;
  }
`;
const Cta = styled(Link)`
  display:inline-flex; align-items:center; justify-content:center; gap:8px;
  padding:14px 28px; border-radius:14px; text-decoration:none; font-weight:600; font-size: 1.05rem;
  background:linear-gradient(135deg, var(--primary), color-mix(in oklab, var(--primary) 85%, var(--gradient-end))); 
  color:#fff; 
  box-shadow: 0 4px 16px color-mix(in oklab, var(--primary) 30%, transparent), 0 2px 4px rgba(0,0,0,.1);
  transition: all .2s ease;
  &:hover { 
    transform: translateY(-2px); 
    box-shadow: 0 6px 20px color-mix(in oklab, var(--primary) 40%, transparent), 0 3px 6px rgba(0,0,0,.12);
  }
  &:active { transform: translateY(0); }
  @media (max-width: 768px) { padding: 12px 24px; font-size: 1rem; }
`;
const Art = styled.div`
  position:relative; flex:0 0 auto; inline-size:clamp(120px, 16vw, 160px); aspect-ratio:1/1; display:grid; place-items:center;
  @media (max-width: 768px) { inline-size:88px; aspect-ratio:1/1; }
`;
const Glow = styled.div`
  position:absolute; inset:0; border-radius:24px;
  background:
    radial-gradient(60% 60% at 50% 30%, color-mix(in oklab, var(--primary) 25%, transparent) 0%, transparent 70%),
    radial-gradient(40% 40% at 70% 70%, color-mix(in oklab, var(--gradient-start) 40%, transparent) 0%, transparent 80%);
  filter: blur(18px); opacity:.7;
  @media (max-width: 768px) { display:none; }
`;
const Img = styled.img`
  width:100%; height:auto; max-width:160px; object-fit:contain;
  filter: drop-shadow(0 10px 18px rgba(0,0,0,.15)); z-index:1;
  @media (max-width: 768px) { max-width:88px; }
`;
